import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";

import { updateFirestoreDoc } from "./firebase";
import { arrayUnion } from "firebase/firestore";

import {
  SLIDE_ADDITIONAL_INFO,
  DISCUSS_PARTNER,
  DIRECTION,
  PLAYER_STATUS,
} from "./constants";
import useSession from "./hooks/useSession";
import { playlistData } from "./playlistData";

import MainView from "./components/MainView";
import Controls from "./components/Controls";
import GlossaryModal from "./components/Glossary/GlossaryModal";

import useTutorialTooltips, {
  TUTORIAL_TOOLTIP_CONFIGS,
} from "./hooks/useTutorialTooltips";

import "./App.css";

const { PREV, NEXT } = DIRECTION;

// Slide Attributes
const PLAY_TIME = "playtime";
const POLL_TIME = "polltime";
const POLL_RESULTS = "pollresults";
const LETS_DISCUSS = "letsdiscuss";

const {
  pollresults: { label: pollresultsLabel },
  playtime: { label: playtimeLabel },
} = SLIDE_ADDITIONAL_INFO;

const isDiscussSlideAttribute = (attribute) =>
  [DISCUSS_PARTNER, LETS_DISCUSS].includes(attribute);

const App = () => {
  const oneClickRef = useRef();
  const gameRef = useRef();
  const [pollResultsId, setPollResultsId] = useState(null);
  const [playtimePhase, setPlaytimePhase] = useState(null);
  const [discussPhase, setDiscussPhase] = useState(null);
  const [isGamePlaying, setIsGamePlaying] = useState(false);
  const [slidesAttributes, setSlidesAttributes] = useState([]);
  const [slidesSections, setSlidesSection] = useState([]);
  const [slidesMarks, setSlidesMarks] = useState([]);
  const [slideNumsToHideSlideDisplay, setSlideNumsToHideSlideDisplay] =
    useState([]);
  // used to detect session id changes to prevent running processSlideAttributes with the outdated slide attributes
  const [loadedSessionId, setLoadedSessionId] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isShowingGlossary, setIsShowingGlossary] = useState(false);

  const [disableArrowControls, setDisableArrowControls] = useState(false);
  const [timerModalInfo, setTimerModalInfo] = useState({
    isVisible: false,
    endTimerEarlyDirection: null,
  });

  const { sessionId } = useParams();
  const {
    currSlideNumber,
    isClassPause = false,
    lessonId,
    pollQuestions,
    pollResults,
    sessionStatsId,
    setCurrSlideNumber,
    slideChangeInfo,
    updateSlideChangeInfo,
    playerStatus,
    currPollNumCompleted,
    customizationNumCompleted,
    setPauseAndClearPrevPoll,
    closePollAndShowResults,
    openPoll,
  } = useSession(sessionId);

  /** @todo refactor teacher stats (w/ platform?!) when more complete scope known */
  useEffect(() => {
    if (sessionStatsId) {
      updateFirestoreDoc("sessionStats", sessionStatsId, {
        pauses: arrayUnion({ isClassPause, timestamp: new Date() }),
      });
    }
  }, [sessionStatsId, isClassPause]);

  const thisPlaylistData = useMemo(
    () => playlistData?.[lessonId]?.data ?? {},
    [lessonId]
  );
  const lessonNum = thisPlaylistData?.lessonNum;

  const tutorialEventTriggerFlags = useMemo(
    () => ({
      isGamePlaying,
      areStudentsPlaying: playerStatus === PLAYER_STATUS.PLAYING,
    }),
    [isGamePlaying, playerStatus]
  );
  const [
    activeTutorialId,
    isTutorialTooltipBlockingInputs,
    completeActiveTutorial,
  ] = useTutorialTooltips(
    lessonNum,
    slidesAttributes[currSlideNumber]?.id,
    timerModalInfo.isVisible,
    [DISCUSS_PARTNER, PLAY_TIME].includes(
      parseSlideId(slidesAttributes[currSlideNumber]?.id).attribute
    ),
    tutorialEventTriggerFlags
  );

  const shouldHideSlideDisplay = useCallback(
    (slideLabel) =>
      [
        pollresultsLabel,
        ...(thisPlaylistData.gameplay ? [playtimeLabel] : []),
      ].includes(slideLabel),
    [thisPlaylistData.gameplay]
  );

  const slideUrl =
    thisPlaylistData.slides ??
    "https://9dots.slides.com/alexiscabrera/one-click-l2-pptx/embed?postMessageEvents=true&style=hidden&byline=hidden&share=hidden";

  const ctrlBarData = useMemo(() => {
    const ctrlBarReturnData = {
      isOnFirstSlide: currSlideNumber <= 0,
      isOnLastSlide: currSlideNumber >= slidesAttributes.length - 1,
    };
    const ctrlBarSectionKeys = [PREV, "curr", NEXT];

    for (
      let i = currSlideNumber - 1, sectionIndex = 0;
      i <= currSlideNumber + 1;
      i++, sectionIndex++
    ) {
      // use one-based indexing for displayed slide numbers
      const currDisplaySlideNumber = i + 1;
      const sectionKey = ctrlBarSectionKeys[sectionIndex];
      if (i >= 0 && i < slidesAttributes.length) {
        ctrlBarReturnData[sectionKey] = {
          slideNumber: currDisplaySlideNumber,
          label: slidesAttributes[i]?.label,
          color: slidesAttributes[i]?.color,
          nextLabel: slidesAttributes[i]?.nextLabel,
        };
      } else {
        ctrlBarReturnData[sectionKey] = {
          slideNumber: currDisplaySlideNumber,
        };
      }
    }

    return ctrlBarReturnData;
  }, [slidesAttributes, currSlideNumber]);

  const handleSlidePostMessage = (method, args) => {
    document
      .getElementById("slide-deck")
      .contentWindow.postMessage(JSON.stringify({ method, args }), "*");
  };

  const startTimerModalCallback = useCallback(() => {
    setPauseAndClearPrevPoll(false);
  }, [setPauseAndClearPrevPoll]);

  const processSlideAttribute = useCallback(
    (slideData) => {
      const { attribute } = slideData;

      let newPollResultsId = null;
      let newPlaytimePhase = null;
      let newDiscussPhase = null;

      if (attribute === "playtime") {
        setPauseAndClearPrevPoll(true);
        if (thisPlaylistData.gameplay?.[slideData.playtimePhase]) {
          newPlaytimePhase = slideData.playtimePhase;
        }
      } else if (attribute === POLL_TIME) {
        openPoll(pollResults, slideData.pollId);
      } else if (attribute === POLL_RESULTS) {
        closePollAndShowResults(slideData.pollId);
        newPollResultsId = slideData.pollId;
      } else if (isDiscussSlideAttribute(attribute)) {
        setPauseAndClearPrevPoll(slideData.discussId);
        // only DISCUSS_PARTNER should set discussPhase
        // because LETS_DISCUSS does not have a discussion timer
        if (attribute === DISCUSS_PARTNER) {
          newDiscussPhase = attribute;
        }
      } else {
        setPauseAndClearPrevPoll(true);
      }

      setPollResultsId(newPollResultsId);
      setDiscussPhase(newDiscussPhase);
      if (thisPlaylistData?.gameplay) {
        setPlaytimePhase(newPlaytimePhase);
      }
    },
    [
      pollResults,
      thisPlaylistData,
      setPauseAndClearPrevPoll,
      closePollAndShowResults,
      openPoll,
    ]
  );

  const handleSlideMessage = useCallback(
    ({ data, origin }) => {
      if (origin === "https://9dots.slides.com") {
        const eventData = JSON.parse(data);
        const { eventName, method } = eventData;

        if (eventName === "ready") {
          // initial slide attributes setup
          handleSlidePostMessage("getSlidesAttributes");

          // sync with other remotes if necessary
          if (typeof slideChangeInfo.newSlideNumber === "number") {
            handleSlidePostMessage("slide", [
              slideChangeInfo.newSlideNumber - 1,
            ]);
          }
        } else if (eventName === "slidechanged") {
          const { indexh } = eventData.state;

          // slide change initiated by slides, not by slidechangeinfo
          /*
            Note this is only necessary if slide changes _can_ be initiated
            by the slides.com embed.
            This has not always been the case,
            and this is not necessarily what we want.
            For now, for soft launch (March 2025),
            this code is necessary because the teacher does have access
            to the slides.com embed.
          */
          if (indexh !== slideChangeInfo.newSlideNumber - 1) {
            const oneIndexedSlide = indexh + 1;
            updateSlideChangeInfo({
              hideSlideDisplay:
                slideNumsToHideSlideDisplay.includes(oneIndexedSlide) ||
                shouldHideSlideDisplay(ctrlBarData.curr.label),
              prevHiderSlideLabel:
                shouldHideSlideDisplay(ctrlBarData.curr.label) &&
                ctrlBarData.curr.label,
              newSlideNumber: oneIndexedSlide,
            });
          }

          // (always) set slide number state
          setCurrSlideNumber(indexh);
        } else if (eventName === "callback") {
          const { result } = eventData;

          if (method === "getSlidesAttributes") {
            const {
              slides,
              sections,
              slidesMarks,
              slideNumsToHideSlideDisplay,
            } = addInfoToSlideAndGetSections(result, shouldHideSlideDisplay);
            setSlidesAttributes(slides);
            setSlidesSection(sections);
            setSlidesMarks(slidesMarks);
            setSlideNumsToHideSlideDisplay(slideNumsToHideSlideDisplay);
            setLoadedSessionId(sessionId);
          }
        }
      }
    },
    [
      ctrlBarData.curr.label,
      sessionId,
      setCurrSlideNumber,
      updateSlideChangeInfo,
      shouldHideSlideDisplay,
      slideChangeInfo,
      slideNumsToHideSlideDisplay,
    ]
  );

  const handleNavigation = useCallback(
    (direction) => {
      const currSlideNumber = ctrlBarData.curr.slideNumber;
      const newSlideNumber =
        direction === PREV && !ctrlBarData.isOnFirstSlide
          ? currSlideNumber - 1
          : direction === NEXT && !ctrlBarData.isOnLastSlide
          ? currSlideNumber + 1
          : currSlideNumber;

      /**
       * hide slide display if direction's slide is poll results, playtime or
       * keep hidden (until slide is ready) if current slide was
       * poll results or playtime
       */
      updateSlideChangeInfo({
        hideSlideDisplay:
          shouldHideSlideDisplay(ctrlBarData[direction].label) ||
          shouldHideSlideDisplay(ctrlBarData.curr.label),
        prevHiderSlideLabel:
          shouldHideSlideDisplay(ctrlBarData.curr.label) &&
          ctrlBarData.curr.label,
        newSlideNumber,
      });
    },
    [updateSlideChangeInfo, ctrlBarData, shouldHideSlideDisplay]
  );

  const handleGameplayClick = useCallback(
    (e) => {
      if (
        isGamePlaying &&
        !["game-pause-btn", "mmss"].includes(e.target.id) &&
        !isTutorialTooltipBlockingInputs
      ) {
        e.preventDefault();
        gameRef.current.contentWindow.focus();
      } else {
        window.focus();
      }
    },
    [isGamePlaying, isTutorialTooltipBlockingInputs]
  );

  const handleFullscreenToggle = useCallback(() => {
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        // Safari <16.4
        document.webkitExitFullscreen();
      }
    } else {
      if (oneClickRef.current.requestFullscreen) {
        oneClickRef.current.requestFullscreen();
      } else if (oneClickRef.current.webkitRequestFullscreen) {
        oneClickRef.current.webkitRequestFullscreen();
      }
    }
  }, [isFullscreen]);

  const handleGlossaryToggle = useCallback(() => {
    setIsShowingGlossary((prev) => !prev);
    if (activeTutorialId === TUTORIAL_TOOLTIP_CONFIGS.GLOSSARY.id) {
      completeActiveTutorial();
    }
  }, [activeTutorialId, completeActiveTutorial]);

  useEffect(() => {
    /*
    Prevent calling a post message twice for the same slide change if prev slide was just showing poll results or playtime
    and state got updated to unhide slide display which occurs in the useEffect below that listens for pollResultsId and playtimePhase changes
    */
    if (
      !slideChangeInfo.hideSlideDisplay &&
      slideChangeInfo.prevHiderSlideLabel
    ) {
      return;
    }
    if (slideChangeInfo.newSlideNumber) {
      handleSlidePostMessage("slide", [slideChangeInfo.newSlideNumber - 1]);
    }
  }, [slideChangeInfo]);

  useEffect(() => {
    const stateByLabels = {
      [pollresultsLabel]: pollResultsId,
      [playtimeLabel]: playtimePhase,
    };
    const prevHiderSlideLabel = slideChangeInfo.prevHiderSlideLabel;
    // poll results or playtime became null and was previously showing, new slide
    // is ready so unhide display if current slide is not playtime or poll results
    if (!stateByLabels[prevHiderSlideLabel] && prevHiderSlideLabel) {
      updateSlideChangeInfo({
        hideSlideDisplay: !!stateByLabels[ctrlBarData.curr.label],
      });
    }
  }, [
    pollResultsId,
    playtimePhase,
    updateSlideChangeInfo,
    slideChangeInfo.prevHiderSlideLabel,
    ctrlBarData.curr.label,
  ]);

  useEffect(() => {
    if (slidesAttributes.length > 0) {
      processSlideAttribute(
        parseSlideId(slidesAttributes[currSlideNumber]?.id)
      );
    }
  }, [currSlideNumber, slidesAttributes, processSlideAttribute]);

  useEffect(() => {
    window.addEventListener("message", handleSlideMessage);

    return () => {
      window.removeEventListener("message", handleSlideMessage);
    };
  }, [handleSlideMessage]);

  useEffect(() => {
    /**
     * NOTE Fullscreen API without vendor specific prefix (i.e. webkit) is not
     * widely available across all browsers (i.e. Safari <16.4) so a check on
     * the element's fullscreen request method is necessary to determine
     * which event listener to add
     *
     * listener on fullscreen change is necessary for updating fullscreen toggle
     * button icon since users can exit fullscreen without using the button and
     * instead use 'ESC' or browser specific UI (i.e. the message browsers
     * display when entering fullscreen)
     */
    if (oneClickRef.current.requestFullscreen) {
      const listener = () => setIsFullscreen(!!document.fullscreenElement);

      window.addEventListener("fullscreenchange", listener);

      return () => {
        window.removeEventListener("fullscreenchange", listener);
      };
    } else if (oneClickRef.current.webkitRequestFullscreen) {
      const listener = () =>
        setIsFullscreen(!!document.webkitFullscreenElement);

      window.addEventListener("webkitfullscreenchange", listener);

      return () => {
        window.removeEventListener("webkitfullscreenchange", listener);
      };
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleGameplayClick);
    return () => {
      document.removeEventListener("click", handleGameplayClick);
    };
  }, [handleGameplayClick]);

  /**
   * NOTE isGamePlaying should only be true if playtimePhase is truthy so
   * resetting isGamePlaying to false is necessary if state and UI gets out
   * of sync which is known to happen when activating teacher play and
   * immediately pressing left/right arrow key casuing a race condition with
   * Gameplay click event and slides.come keydown event
   */
  useEffect(() => {
    if (!playtimePhase && isGamePlaying) {
      setIsGamePlaying(false);
    }
  }, [isGamePlaying, playtimePhase]);

  return (
    <div id="one-click" ref={oneClickRef}>
      {/** @todo update remote and player status UI? */}
      <MainView
        hideSlideDisplay={slideChangeInfo.hideSlideDisplay}
        pollQuestion={pollQuestions[pollResultsId]}
        pollResults={pollResults[pollResultsId]}
        slideUrl={validateAndFormatUrl(slideUrl)}
        handleSlidePostMessage={handleSlidePostMessage}
        currPollNumCompleted={currPollNumCompleted}
        gameRef={gameRef}
        playtimePhase={playtimePhase}
        discussPhase={discussPhase}
        gameplayRoute={thisPlaylistData?.gameplay?.[playtimePhase]}
        isGamePlaying={isGamePlaying}
        setIsGamePlaying={setIsGamePlaying}
        timerTimes={thisPlaylistData?.timerTimes}
        setDisableArrowControls={setDisableArrowControls}
        timerModalInfo={timerModalInfo}
        setTimerModalInfo={setTimerModalInfo}
        handleNavigation={handleNavigation}
        nextSlideLabel={ctrlBarData.next.label}
        startTimerModalCallback={startTimerModalCallback}
        isShowingGlossary={isShowingGlossary}
        isTutorialTooltipBlockingInputs={isTutorialTooltipBlockingInputs}
        completeActiveTutorial={completeActiveTutorial}
        customizationNumCompleted={customizationNumCompleted}
        lessonNum={lessonNum}
      />
      <Controls
        disableArrowControls={disableArrowControls}
        ctrlBarData={ctrlBarData}
        updateSlideChangeInfo={updateSlideChangeInfo}
        numOfSlides={slidesAttributes.length}
        isFullscreen={isFullscreen}
        isShowingGlossary={isShowingGlossary}
        handleFullscreenToggle={handleFullscreenToggle}
        handleGlossaryToggle={handleGlossaryToggle}
        playerStatus={playerStatus}
        slideSections={slidesSections}
        slidesMarks={slidesMarks}
        slideNumsToHideSlideDisplay={slideNumsToHideSlideDisplay}
        thumbnailImages={thisPlaylistData?.thumbnail}
        isGamePlaying={isGamePlaying}
        setIsGamePlaying={setIsGamePlaying}
        playtimePhase={playtimePhase}
        shouldHideSlideDisplay={shouldHideSlideDisplay}
        timerModalInfo={timerModalInfo}
        setTimerModalInfo={setTimerModalInfo}
        handleNavigation={handleNavigation}
        activeTutorialId={activeTutorialId}
        isTutorialTooltipBlockingInputs={isTutorialTooltipBlockingInputs}
        completeActiveTutorial={completeActiveTutorial}
        isSessionLoaded={!!loadedSessionId}
      />
      <GlossaryModal
        isVisible={isShowingGlossary}
        handleGlossaryToggle={handleGlossaryToggle}
        lessonNum={lessonNum ?? 1}
      />
    </div>
  );
};

export default App;

// utility to return slide attribute and unique id which can be a pollId or playtimeId
// note: assumes pollId is kebab-case
function parseSlideId(slideId) {
  const slideData = {};
  const match = slideId?.match(/^(.*?)-(.*?)$/);

  if (match) {
    slideData.attribute = match[1];

    if ([POLL_TIME, POLL_RESULTS].includes(match[1])) {
      slideData.pollId = match[2];
    } else if (isDiscussSlideAttribute(match[1])) {
      slideData.discussId = slideId;
    } else if (PLAY_TIME === match[1]) {
      slideData.playtimePhase = match[2];
    }
  } else {
    slideData.attribute = slideId;
  }

  return slideData;
}

function getColorAndLabelObj(slideId) {
  if (slideId?.match(/(poll|playtime|letsdiscuss|discusspartner).*/)) {
    const { attribute } = parseSlideId(slideId);
    return (
      SLIDE_ADDITIONAL_INFO[attribute] ?? {
        label: null,
        sectionColor: null,
        nextLabel: null,
        notch: false,
      }
    );
  }

  return SLIDE_ADDITIONAL_INFO[slideId] ?? { label: null, sectionColor: null };
}

function addInfoToSlideAndGetSections(slides, shouldHideSlideDisplay) {
  const sections = [];
  const slidesMarks = [];
  const slideNumsToHideSlideDisplay = [];
  let currColor = "";
  for (let i = 0; i < slides.length; i++) {
    const { label, nextLabel, sectionColor, notch } = getColorAndLabelObj(
      slides[i]?.id
    );

    // update color for this section
    if (sectionColor) {
      currColor = sectionColor;

      // if section color, means start of new section
      sections.push({
        color: currColor,
        cummulativeSectionLength: i / (slides.length - 1),
      });
    }

    if (notch) {
      // add 1 to value to match one-based indexing for displayed slide numbers
      slidesMarks.push({
        value: i + 1,
        color: currColor,
        text: label,
      });
    }

    if (shouldHideSlideDisplay(label)) {
      // add 1 to value to match one-based indexing
      slideNumsToHideSlideDisplay.push(i + 1);
    }

    slides[i].color = currColor;
    slides[i].label = label;
    slides[i].nextLabel = nextLabel;
  }
  return { slides, sections, slidesMarks, slideNumsToHideSlideDisplay };
}

function validateAndFormatUrl(slideUrl) {
  if (!slideUrl) {
    throw new Error("Missing Url");
  } else if (typeof slideUrl !== "string") {
    throw new Error("Url is not a string");
  }

  const url = new URL(slideUrl);

  if (url.origin === "https://9dots.slides.com") {
    // if formatted properly should split up to [empty, username, slidename, maybe embeded, empty]
    const [, username, slidename] = url.pathname.split("/");

    if (!username || !slidename) {
      throw new Error("Cannot embed slides, incorrect slide url format.");
    }

    url.pathname = `/${username}/${slidename}/embed/`;

    url.searchParams.set("postMessageEvents", true);
    url.searchParams.set("style", "hidden");
    url.searchParams.set("byline", "hidden");
    url.searchParams.set("share", "hidden");

    return url.href;
  } else {
    throw new Error("Embed slides is not from slides.com.");
  }
}
